import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import type { AgrupacionArticulos } from '../../stores/agrupaciones-articulos/agrupacion-articulos';
import type {
  ActualizarAgrupacionArticulos,
  RegistrarAgrupacionArticulos
} from '../../stores/agrupaciones-articulos/agrupaciones-articulos.store';
import { useGrupoArticulosStore } from '../../stores/grupos-articulos/grupos-articulos.store';
import { useUIStore } from '../../stores/ui/ui.store';
import type { CommonEntityFormProps } from '../../utils/common-entity-form-props';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import transformToOptions from '../../utils/transform-to-options';
import AppTextField from '../ui/AppTextField';
import ComboBox from '../ui/Combobox';
import EditForm from '../ui/EditForm';

const validationSchema = yup.object({
  id: yup.string().required('La clave es requerida'),
  nombre: yup.string().required('El nombre es requerido'),
  idGrupo: yup.number().moreThan(0, 'El grupo es requerido')
});

type AgrupacionArticulosFormProps = {
  agrupacion: AgrupacionArticulos | null;
  onSave:
    | ((values: ActualizarAgrupacionArticulos) => Promise<void>)
    | ((values: RegistrarAgrupacionArticulos) => Promise<boolean>);
} & CommonEntityFormProps;

const AgrupacionArticulosForm = ({
  agrupacion,
  esAgregar,
  onSave,
  onDelete
}: AgrupacionArticulosFormProps) => {
  const esEditar = !esAgregar;
  const { loading } = useUIStore((state) => state);
  const { actives: grupos, cargarActivos } = useGrupoArticulosStore((state) => state);
  const initialValues = {
    id: agrupacion?.id ?? 0,
    nombre: agrupacion?.nombre ?? '',
    idGrupo: agrupacion?.idGrupo ?? 0
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const success = await onSave(values);
      if (success) {
        formik.resetForm();
      }
    },
    enableReinitialize: true
  });

  useEffect(() => {
    cargarActivos();
  }, []);

  const gruposOptions = transformToOptions(grupos);

  const reset = () => formik.resetForm({ values: initialValues });

  return (
    <EditForm
      module="agrupaciones-articulos"
      title="Agrupación de Artículos"
      readonly={false}
      returnLink="/grupos-agrupaciones-articulos/agrupaciones"
      loading={loading}
      showActivate={false}
      showDelete={esEditar}
      onSave={formik.handleSubmit}
      onClear={reset}
      onDelete={onDelete}
    >
      <Grid>
        <Grid container spacing={2}>
          <Grid>
            <AppTextField
              name="id"
              label="Clave"
              placeholder="Clave"
              value={formik.values.id}
              disabled={true}
            />
          </Grid>
          <Grid size="grow">
            <AppTextField
              name="nombre"
              label="Nombre"
              placeholder="Nombre"
              maxLength={40}
              disabled={loading}
              value={formik.values.nombre}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.nombre && Boolean(formik.errors.nombre)}
              helperText={getLengthOrErrorText(
                formik.touched.nombre ? formik.errors.nombre : '',
                formik.values.nombre.length,
                40
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Grid container spacing={2}>
          <Grid size="grow">
            <ComboBox
              label="Grupo"
              name="idGrupo"
              disabled={loading}
              value={formik.values.idGrupo}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              options={gruposOptions}
              error={formik.touched.idGrupo && Boolean(formik.errors.idGrupo)}
              helperText={formik.touched.idGrupo ? formik.errors.idGrupo : ''}
            />
          </Grid>
        </Grid>
      </Grid>
    </EditForm>
  );
};

export default AgrupacionArticulosForm;
